import { useEffect, useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { Input, Select } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { RoomTheme, RoomThemeCategory } from "../api/types";
import { UploadButton } from "../components/ui/UploadButton";

// Round 6 point 2 — the catalog behind the Android app's Room Settings
// Theme picker (GET /rooms/themes, room-profile.js), which only ever
// shows is_active=true rows here. coins_price here is the ONLY source of
// truth for what a paid theme costs — nothing about pricing is hardcoded
// in the app.
export function RoomThemesPage() {
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } = usePagedList<RoomTheme>("/admin/room-themes");
  const [editing, setEditing] = useState<Partial<RoomTheme> | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();
  const [categories, setCategories] = useState<RoomThemeCategory[]>([]);
  useEffect(() => {
    apiRequest<RoomThemeCategory[]>("/admin/room-theme-categories").then(setCategories).catch(() => setCategories([]));
  }, []);
  const categoryName = (id?: number | null) => categories.find((c) => c.id === id)?.name ?? "—";

  async function save() {
    if (!editing?.name) {
      showToast("Name is required", "error");
      return;
    }
    if (!editing.is_free && !(Number(editing.coins_price) > 0)) {
      showToast("A paid theme needs a positive coin price", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      const body = {
        name: editing.name,
        preview_url: editing.preview_url || null,
        is_free: !!editing.is_free,
        coins_price: editing.is_free ? null : Number(editing.coins_price),
        duration_days: editing.duration_days ? Number(editing.duration_days) : null,
        is_active: editing.is_active ?? true,
        category_id: editing.category_id ?? null,
        sort_order: Number(editing.sort_order) || 0,
      };
      if (editing.id) {
        await apiRequest(`/admin/room-themes/${editing.id}`, { method: "PATCH", body });
      } else {
        await apiRequest("/admin/room-themes", { method: "POST", body });
      }
      showToast("Saved", "success");
      reload();
      setEditing(null);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save theme", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function toggleActive(row: RoomTheme) {
    try {
      await apiRequest(`/admin/room-themes/${row.id}`, { method: "PATCH", body: { is_active: !row.is_active } });
      showToast(row.is_active ? "Deactivated" : "Activated", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not update theme", "error");
    }
  }

  const columns: Column<RoomTheme>[] = [
    {
      header: "Theme",
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.preview_url ? (
            <img src={row.preview_url} className="h-10 w-10 rounded-lg object-cover" alt="" />
          ) : (
            <div className="h-10 w-10 rounded-lg bg-surface-muted" />
          )}
          <p className="font-medium text-ink">{row.name}</p>
        </div>
      ),
    },
    {
      header: "Price",
      render: (row) =>
        row.is_free ? (
          <Badge tone="success">Free</Badge>
        ) : (
          `${row.coins_price} coins`
        ),
    },
    { header: "Category", render: (row) => categoryName(row.category_id) },
    { header: "Duration", render: (row) => (row.duration_days ? `${row.duration_days} days` : "Permanent") },
    { header: "Status", render: (row) => (row.is_active ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Inactive</Badge>) },
    {
      header: "",
      className: "text-right",
      render: (row) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={() => setEditing(row)}>
            Edit
          </Button>
          <Button size="sm" variant={row.is_active ? "danger" : "secondary"} onClick={() => toggleActive(row)}>
            {row.is_active ? "Deactivate" : "Activate"}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AppShell title="Room Themes">
      <p className="mb-4 text-xs text-ink-faint">
        This is the real catalog the Android app's Room Settings Theme picker reads from — nothing here is
        hardcoded in the app. Deactivating a theme removes it from the picker immediately without deleting any
        room that already applied it (there's no delete here on purpose — a theme a room already paid for and
        applied should stay a real row, not disappear).
      </p>
      <div className="mb-4 flex justify-end">
        <Button onClick={() => setEditing({ is_free: false, is_active: true })}>+ Add Theme</Button>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No room themes yet"
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />

      <Modal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit Theme" : "Add Theme"}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={save} isLoading={isSubmitting}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label="Internal name (admin only — never shown in the app)" value={editing?.name ?? ""} onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))} />
          <Input
            label="Full-art background image URL (what users see)"
            value={editing?.preview_url ?? ""}
            onChange={(e) => setEditing((p) => ({ ...p, preview_url: e.target.value }))}
          />
          <UploadButton kind="room_theme" onUploaded={(u) => setEditing((p) => ({ ...p, preview_url: u }))} />
          <Select
            label="Category (tab in the app)"
            value={editing?.category_id ? String(editing.category_id) : ""}
            onChange={(e) => setEditing((p) => ({ ...p, category_id: e.target.value ? Number(e.target.value) : null }))}
          >
            <option value="">— none (hidden in the app) —</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}{c.is_visible ? "" : " (hidden)"}</option>
            ))}
          </Select>
          <Input
            label="Sort order within category"
            type="number"
            value={editing?.sort_order ?? 0}
            onChange={(e) => setEditing((p) => ({ ...p, sort_order: Number(e.target.value) }))}
          />
          <Select
            label="Free or paid"
            value={editing?.is_free ? "free" : "paid"}
            onChange={(e) => setEditing((p) => ({ ...p, is_free: e.target.value === "free" }))}
          >
            <option value="free">Free</option>
            <option value="paid">Paid</option>
          </Select>
          {!editing?.is_free && (
            <Input
              label="Coin price"
              type="number"
              value={editing?.coins_price ?? ""}
              onChange={(e) => setEditing((p) => ({ ...p, coins_price: Number(e.target.value) }))}
            />
          )}
          <Select
            label="Duration"
            value={editing?.duration_days ? String(editing.duration_days) : "permanent"}
            onChange={(e) => setEditing((p) => ({ ...p, duration_days: e.target.value === "permanent" ? null : Number(e.target.value) }))}
          >
            <option value="permanent">Permanent</option>
            <option value="1">1 day</option>
            <option value="7">7 days</option>
            <option value="30">30 days</option>
          </Select>
        </div>
      </Modal>
    </AppShell>
  );
}
